(ns hai.notes.detail
  "Full-page view of a single note as the shared editor surface — the same
   one the edit/create modals show: the composer with its Save and Cancel,
   the share footer, and the header ⋮ holding Delete."
  (:require
   [clojure.string :as str]
   [hai.entity.data :as entity-data]
   [hai.entity.draft :as draft]
   [hai.sharing.helper :as sharing]
   [hai.spaces.data :as spaces-data]
   [hai.notes.data :as data]
   [hai.notes.editor :as editor]
   [hai.notes.form :as form]
   [hai.ui.components :as c]
   [hai.ui.components.read-only-cue :as read-only-cue]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.web-app.viewport :as viewport]
   [lambdaisland.ornament :as o]))

(defn- note-label
  "First non-empty line of a note, truncated. Falls back to \"Note\" for an
   empty or not-yet-loaded note."
  [note]
  (let [first-line (some->> (:note/text note)
                            str/split-lines
                            (map str/trim)
                            (some not-empty))]
    (cond
      (nil? first-line)         "Note"
      (> (count first-line) 60) (str (subs first-line 0 60) "…")
      :else                     first-line)))

(defsub ::window-title
  [db]
  (let [note-uuid (get-in db [:routing/current-route :path-params :note/uuid])
        note (when note-uuid (get-in db [:note/uuid (uuid (str note-uuid))]))]
    (note-label note)))

(defn draft-id
  "The draft behind one note's page. Per note, so opening a second one in
   another tab doesn't edit the first one's."
  [note-uuid]
  [::draft (uuid (str note-uuid))])

;; Seeded when the route's resolve lands and re-seeded by Cancel; the route's
;; :stop discards it.
(defevent ::seed-draft [db note-uuid]
  (draft/seed db
              (draft-id note-uuid)
              (form/from-note (get-in db [:note/uuid (uuid (str note-uuid))]))))

(defevent ::discard-draft [db note-uuid]
  (draft/discard db (draft-id note-uuid)))

;; Tracks which notes a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both leave a nil entity.
(defevent ::mark-resolved [db note-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str note-uuid))))

(defsub ::resolved?
  [db note-uuid]
  (boolean (and note-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str note-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

;; The dialog caps the modal surface at 900px and floats it over a dimmed
;; page; here the column mirrors the cap and a shadow provides the elevation
;; the overlay would.
(o/defstyled editor-column :div
  {:max-width "900px"}
  [editor/edit-modal
   {:box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 12px 32px rgba(45,62,42,0.16)"}])

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn- note-view [note]
  (let [note-uuid   (:note/uuid note)
        can-edit?   (sharing/can-edit? note)
        mobile?     @(<s [::viewport/mobile?])
        saving?     (= :start @(<s [:fetch/state [::data/save-edit note-uuid]]))
        spaces?     @(<s [::spaces-data/enabled?])
        delete! (fn []
                  (>e [::data/delete note-uuid])
                  (>e [:routing/navigate [:notes/view]]))
        editor-props
        {:title           (if can-edit? "Editing note" "Note")
         :note            note
         :draft-id        (draft-id note-uuid)
         :editing?        can-edit?
         :share?          (sharing/can-share? note)
         :spaces?         spaces?
         :auto-focus?     false
         :show-hint?      (not mobile?)
         :saving?         saving?
         ;; The page reads the note from the entity store, where the save's
         ;; response lands.
         :on-save         (fn [staged] (>e [::data/save-edit note-uuid staged]))
         ;; Cancel puts the draft back to the note as last saved.
         :on-cancel       #(>e [::seed-draft note-uuid])
         :on-open-entity  entity-data/open-entity
         :on-share-closed #(>e [:entity/resolve {:note/uuid note-uuid}])
         :on-close        #(>e [:routing/navigate [:notes/view]])
         :on-delete       delete!}]
    (if mobile?
      ;; The same full-screen surface the home card expands into; ✕ returns
      ;; to the wall.
      [editor/screen editor-props]
      [editor-column
       ;; The owner always edits, so a viewer who cannot edit never deletes.
       (when-not can-edit?
         [read-only-cue/cue "This note is read-only"])
       [editor/modal-content editor-props]])))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        note      @(<s [:entity params])
        resolved? @(<s [::resolved? (:note/uuid params)])]
    [layout/page
     [layout/content
      (cond
        ;; The composer edits the draft the route seeded, and stays put across
        ;; saves — each diffs against the note as the store holds it then — so
        ;; a landing save never interrupts the typing.
        note      [note-view note]
        resolved? [muted "This note doesn't exist or you no longer have access to it."]
        :else     [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
